import { useEffect, useState } from 'react';
import type { CheckoutOrganizationCompany } from '../../types/checkout';

interface BillingSectionProps {
  companies: CheckoutOrganizationCompany[];
  selectedCompanyId: number | null;
  billingChosen: boolean;
  personalDisabled?: boolean;
  discountWarning?: string | null;
  onSelect: (companyId: number | null) => void;
  onContinue: () => void;
  isLoading: boolean;
}

export function BillingSection({
  companies,
  selectedCompanyId,
  billingChosen,
  personalDisabled,
  discountWarning,
  onSelect,
  onContinue,
  isLoading,
}: BillingSectionProps) {
  // Derive initial selection: if billingChosen and no company, it means personal
  const [chosen, setChosen] = useState<number | 'personal' | null>(
    selectedCompanyId !== null ? selectedCompanyId : billingChosen ? 'personal' : null
  );

  // Auto-select when personal is disabled and there's only one company
  useEffect(() => {
    if (personalDisabled && companies.length === 1 && chosen === null) {
      setChosen(companies[0].id);
      onSelect(companies[0].id);
    }
  }, [personalDisabled, companies, chosen, onSelect]);

  const handleSelect = (value: number | 'personal') => {
    setChosen(value);
    onSelect(value === 'personal' ? null : value);
  };

  return (
    <div>
      <p className="mb-4 text-sm text-gray-500">
        Seleccioná a nombre de quién facturamos este pedido
      </p>

      <div className="mb-4 flex flex-col gap-2">
        {companies.map((company) => (
          <button
            key={company.id}
            type="button"
            className={`flex w-full items-start gap-3 rounded-xl border-2 p-3.5 text-left transition-all duration-200
              ${chosen === company.id ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100'}
              cursor-pointer`}
            onClick={() => handleSelect(company.id)}
          >
            <div className="flex-shrink-0 pt-0.5">
              {chosen === company.id ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#5cb85c">
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="4" fill="white" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                </svg>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-medium leading-snug text-gray-800">
                {company.display_name}
              </div>
              <div className="mt-1 text-[13px] text-gray-500">
                {company.cuit && `CUIT ${company.cuit}`}
                {company.cuit && company.iva_condition_name && ' · '}
                {company.iva_condition_name}
              </div>
            </div>
          </button>
        ))}

        {/* Personal option — hidden when personal purchases are disabled */}
        {!personalDisabled && (
          <button
            type="button"
            className={`flex w-full items-start gap-3 rounded-xl border-2 p-3.5 text-left transition-all duration-200
              ${chosen === 'personal' ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100'}
              cursor-pointer`}
            onClick={() => handleSelect('personal')}
          >
            <div className="flex-shrink-0 pt-0.5">
              {chosen === 'personal' ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#5cb85c">
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="4" fill="white" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                </svg>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-medium leading-snug text-gray-800">
                Personal
              </div>
              <div className="mt-1 text-[13px] text-gray-500">
                Sin empresa
              </div>
            </div>
          </button>
        )}
      </div>

      {discountWarning && (
        <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-amber-300 bg-amber-50 p-3">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 flex-shrink-0 text-amber-500">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <p className="text-sm text-amber-800">{discountWarning}</p>
        </div>
      )}

      <button
        type="button"
        className="w-full rounded-lg bg-green-500 px-5 py-3.5 text-base font-semibold text-white transition-colors hover:bg-green-600 disabled:cursor-not-allowed disabled:bg-gray-300"
        onClick={onContinue}
        disabled={isLoading || chosen === null}
      >
        Continuar
      </button>
    </div>
  );
}
